import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Skeleton, Card, CardContent, CardMedia, useTheme } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { baseUrl, Endpoints } from '../api';
import { useAppSelector } from '../redux/hooks';
import { selectUserId } from '../redux/slices/user';
import { postData } from '../redux/thunks/itemsApi';
import AppText from './atoms/AppText';
import './Appcard.css';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { CurrencyCode, currencyLocaleMap } from '../schemes/items';
import { formatPrice } from '../utils/money';

interface IProps {
  type: 'top' | 'normal' | 'preview';
  details:
  | {
    title: string;
    image: string;
    originalImage: string;
    itemStatus: string;
    price: number;
    currency: string;
    city: string;
    createdDate: string;
    like: boolean | number;
    id: number;
    userId: number;
    convertedCurrency?: string;
    convertedPrice?: number;
  }
  | any;
  marginHorizontal?: number;
}

function AppCard({ type, details }: IProps) {
  const theme = useTheme();
  const navigation = useNavigate();
  const userId = useAppSelector(selectUserId);
  const { title, image, itemStatus, price, currency, city, createdDate, like, id: itemId, convertedCurrency, convertedPrice } = details;

  const [isLiked, setIsLiked] = useState(() => {
    return typeof like === 'number' ? like !== 0 : like;
  });
  const [isFavIconDisabled, setIsFavIconDisabled] = useState(userId === details.userId);

  const getFavoriteIconColor = useMemo(() => {
    if (isLiked) {
      return theme.palette.error.main; // Usually a shade of red
    }
    // When not liked, use a more visible color
    return theme.palette.mode === 'light'
      ? theme.palette.grey[400] // Lighter grey for light mode
      : theme.palette.grey[600]; // Darker grey for dark mode
  }, [isLiked, theme]);

  const imgUrl = useMemo(() => {
    switch (type) {
      case 'preview':
        return image;
      case 'top':
        return Endpoints.photoOriginal + image;
      default:
        return Endpoints.photoThumb + image;
    }
  }, [type, image]);

  // usage
  const priceTxt = formatPrice(convertedPrice ?? price, (convertedCurrency ?? currency) as CurrencyCode);

  const likeApiPost = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!userId) {
        alert('Please login');
        return;
      }

      setIsFavIconDisabled(true);
      try {
        const response = await postData(`${baseUrl}/Items/SetItemLike`, { itemId, userId });
        if (response) {
          setIsLiked((current: any) => !current);
        }
      } catch (error) {
      } finally {
        setIsFavIconDisabled(false);
      }
    },
    [userId, itemId]
  );

  const navigateToItemDetails = useCallback(() => {
    navigation(`/details/${itemId}`);
  }, [navigation, itemId]);

  return (
    <a
      href={`/details/${itemId}`}
      style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flex: 1, minHeight: 0, width: '100%' }}
      onClick={(e) => {
        e.preventDefault();
        navigation(`/details/${itemId}`);
      }}
    >
      <Card
        className="app-card"
        sx={{
          ...styles.cardStyle,
          flex: 1,
          width: '100%',
          backgroundColor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[1],
          cursor: 'pointer',
          '&:hover': {
            boxShadow: theme.shadows[2],
            transform: 'translateY(-1px)',
            transition: 'all 0.2s ease-in-out'
          },
          ...(type === 'top' ? styles.topCardStyle : {})
        }}
      >
        {!imgUrl ? <Skeleton variant="rounded" sx={styles.skeletonStyle} animation="wave" /> : <CardMedia component="img" alt={title} image={imgUrl} sx={styles.imageStyle} loading="lazy" />}
        <CardContent sx={styles.contentStyle}>
          <Box sx={styles.titleBlockStyle}>
            <AppText textSize={12} style={styles.titleStyle} onClick={navigateToItemDetails}>
              {title}
            </AppText>
          </Box>

          <Box sx={styles.metaBlockStyle}>
            <AppText textSize={13} style={styles.priceStyle}>
              {priceTxt}
            </AppText>

            <AppText textSize={11} style={styles.addressStyle}>
              {city}, {createdDate}
            </AppText>
          </Box>

          <Box sx={styles.itemStatusStyle}>
            <AppText textSize={11} style={styles.statusTextStyle}>
              {itemStatus}
            </AppText>

            <IconButton
              aria-label={isLiked ? 'Remove from favorites' : 'Add to favorites'}
              size="small"
              sx={styles.favoriteButton}
              onClick={likeApiPost}
              disabled={isFavIconDisabled}
            >
              {type !== 'preview' &&
                (isLiked ? (
                  <FavoriteIcon fontSize="small" sx={{ color: getFavoriteIconColor }} />
                ) : (
                  <FavoriteBorderIcon fontSize="small" sx={{ color: getFavoriteIconColor }} />
                ))}
            </IconButton>
          </Box>
        </CardContent>
      </Card>
    </a>
  );
}

export default React.memo(AppCard);

const styles = {
  cardContainerStyle: {
    display: 'flex',
    flexDirection: 'column',
    mx: { xs: 0, sm: 0 },
    py: 1,
    width: '100%',
    maxWidth: '100%'
  },
  addressStyle: {
    m: 0,
    color: 'text.secondary',
    fontWeight: 500,
    letterSpacing: -0.3,
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 1.3
  },
  priceStyle: {
    m: 0,
    fontWeight: 700,
    fontStyle: 'normal',
    color: 'primary.main',
    letterSpacing: -0.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 1.3
  },
  cardStyle: {
    width: '100%',
    maxWidth: '100%',
    height: '100%',
    minHeight: { xs: 248, sm: 300, md: 330 },
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    borderRadius: 2,
    boxShadow: 1,
    overflow: 'hidden'
  },
  topCardStyle: {
    border: '2px solid',
    borderColor: 'warning.main',
    backgroundColor: 'action.hover',
    height: '100%',
    minHeight: 'auto'
  },
  imageStyle: {
    width: '100%',
    aspectRatio: '1',
    flexShrink: 0,
    objectFit: 'cover',
    cursor: 'pointer'
  },
  skeletonStyle: {
    width: '100%',
    aspectRatio: '1',
    flexShrink: 0,
    borderRadius: 0
  },
  contentStyle: {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    textAlign: 'left',
    p: 1,
    pt: 1,
    '&:last-child': {
      pb: 1
    }
  },
  /** Always reserve two title lines so the footer row aligns across cards */
  titleBlockStyle: {
    flexShrink: 0,
    minHeight: { xs: 27, sm: 36 },
    mb: 0.5
  },
  titleStyle: {
    m: 0,
    lineHeight: 1.5,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textAlign: 'left',
    letterSpacing: 0.1,
    color: 'text.primary',
    fontWeight: 500,
    cursor: 'pointer'
  },
  metaBlockStyle: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.25
  },
  itemStatusStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 0.5,
    marginTop: 'auto',
    flexShrink: 0,
    minHeight: 32,
    pt: 0.5
  },
  statusTextStyle: {
    m: 0,
    flex: 1,
    minWidth: 0,
    fontWeight: 500,
    color: 'text.secondary',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 1.3
  },
  favoIconStyle: {
    textAlign: 'left',
    boxShadow: 0,
    '&:hover': {
      bgcolor: 'transparent'
    }
  },
  favoriteButton: {
    flexShrink: 0,
    width: 32,
    height: 32,
    p: 0.5,
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'scale(1.1)',
      backgroundColor: 'transparent'
    },
    '&:active': {
      transform: 'scale(0.9)'
    }
  }
};
